// src/components/organisms/sidebar/sidebar.jsx
import classnames from 'classnames'
import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { Link, NavLink as NavLinkRRD, useLocation, useHistory } from 'react-router-dom'
import { Button, Collapse, Nav, Navbar, NavbarBrand, NavItem, NavLink } from 'reactstrap'
import AdminsApiV2 from 'api/v2/admins-api-v2.js'
import OutboundInternalRequestsApiV2 from 'api/v2/admins/outbound-internal-requests-api-v2'
import ConsignorSettlementsApiV2 from 'api/v2/admins/consignor-settlements-api-v2'
import { useAuth } from 'providers/auth-provider'
import { fetchPendingTasks } from 'api/v2/admins/pending-tasks-api'

const propTypes = {
  toggleSidebar : PropTypes.func,
  isSidebarOpen : PropTypes.bool,
  routes        : PropTypes.arrayOf(PropTypes.object),
  logo          : PropTypes.shape({
    name      : PropTypes.string,
    sup       : PropTypes.string,
    innerLink : PropTypes.string,
    outterLink: PropTypes.string,
    imgSrc    : PropTypes.string.isRequired,
    imgAlt    : PropTypes.string.isRequired
  })
}

const normalizeSidebarRole = (role) => role?.toString().trim().toLowerCase().replace(/\s+/g, '_') || ''

const Sidebar = ({ logo, routes, isSidebarOpen, toggleSidebar }) => {
  const [collapseStates, setCollapseStates] = useState({})
  const [hoverItem, setHoverItem] = useState(null)
  const [internalRequestNotificationCounts, setInternalRequestNotificationCounts] = useState({})
  const [settlementNotificationCounts, setSettlementNotificationCounts] = useState({})
  const [pendingTaskCounts, setPendingTaskCounts] = useState({})
  const history = useHistory()
  const location = useLocation()
  const { user } = useAuth()
  const currentRole = normalizeSidebarRole(user?.role)
  const currentEmail = user?.email?.toString().trim().toLowerCase() || ''

  const isRouteAllowed = (route) => {
    const allowedRoles = Array.isArray(route?.allowedRoles) ? route.allowedRoles : []
    const allowedEmails = Array.isArray(route?.allowedEmails) ? route.allowedEmails : []

    if (allowedRoles.length === 0 && allowedEmails.length === 0) return true

    const roleAllowed = allowedRoles.some((role) => normalizeSidebarRole(role) === currentRole)
    const emailAllowed = allowedEmails.some((email) => email.toString().trim().toLowerCase() === currentEmail)

    return roleAllowed || emailAllowed
  }

  const hasVisibleRoute = (routesArr) => (routesArr || []).some((route) => {
    if (!route || route.redirect || route.invisible || !isRouteAllowed(route)) return false
    if (route.collapse) return hasVisibleRoute(route.views)

    return true
  })
  const authContext = useAuth()

  // === Active route detection ===
  const isRouteActive = (route) => {
    if (!route || route.invisible || !isRouteAllowed(route)) return false
    if (route.collapse && route.views) {
      return route.views.some((child) => isRouteActive(child))
    }
    if (!route.layout || !route.path) return false
    const fullPath = `${route.layout}${route.path}`
    return (
      location.pathname === fullPath ||
      location.pathname.startsWith(`${fullPath}/`)
    )
  }

  // true if any descendant is active (used for initial open)
  const getCollapseInitialState = (routesArr) => {
    if (!routesArr) return false
    for (let i = 0; i < routesArr.length; i++) {
      const r = routesArr[i]
      if (isRouteActive(r)) return true
      if (r.collapse && r.views && getCollapseInitialState(r.views)) return true
    }
    return false
  }

  // build a map: { state_key: shouldBeOpen }
  const getCollapseStates = (routesArr) => {
    let initialState = {}
    routesArr.forEach((route) => {
      if (route.collapse) {
        if (!route.state_key) {
          // hard requirement: unique state_key for every collapse group
          // no throw; just skip to avoid setting "undefined" key
        } else {
          initialState = {
            ...initialState,
            [route.state_key]: getCollapseInitialState(route.views),
            ...getCollapseStates(route.views || [])
          }
        }
      }
    })
    return initialState
  }

  // initial mount cleanup + initial compute
  useEffect(() => {
    setCollapseStates(getCollapseStates(routes || []))
    document.body.classList.remove('g-sidenav-show')
    return () => document.body.classList.remove('g-sidenav-show')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 🔧 FIX: when URL changes, ensure parents of the active page are opened.
  useEffect(() => {
    const auto = getCollapseStates(routes || [])
    // merge (preserve any user-opened groups; auto-open active parents)
    setCollapseStates((prev) => ({ ...prev, ...auto }))
  }, [location.pathname, routes]) // if routes are hot-reloaded, also recompute

  useEffect(() => {
    let isMounted = true
    let isFetching = false
    setInternalRequestNotificationCounts({})

    const fetchInternalRequestNotificationCounts = () => {
      if (!isMounted || isFetching || !currentEmail) return
      isFetching = true
      OutboundInternalRequestsApiV2.notificationCounts()
        .then((response) => {
          if (isMounted) setInternalRequestNotificationCounts(response.data.notification_counts || {})
        })
        .catch(() => {
          if (isMounted) setInternalRequestNotificationCounts({})
        })
        .finally(() => { isFetching = false })
    }

    fetchInternalRequestNotificationCounts()
    const refreshInterval = window.setInterval(fetchInternalRequestNotificationCounts, 60000)
    window.addEventListener('focus', fetchInternalRequestNotificationCounts)
    window.addEventListener('internal-request-updated', fetchInternalRequestNotificationCounts)

    return () => {
      isMounted = false
      window.clearInterval(refreshInterval)
      window.removeEventListener('focus', fetchInternalRequestNotificationCounts)
      window.removeEventListener('internal-request-updated', fetchInternalRequestNotificationCounts)
    }
  }, [location.pathname, currentRole, currentEmail])

  useEffect(() => {
    let active = true
    let fetching = false
    setSettlementNotificationCounts({})
    const refresh = () => {
      if (!active || fetching || !currentEmail) return
      if (!['owner', 'admin', 'product_curated', 'purchasing'].includes(currentRole) && currentEmail !== 'jac@belleandkate.com') return
      fetching = true
      ConsignorSettlementsApiV2.notificationCounts()
        .then(({ data }) => { if (active) setSettlementNotificationCounts(data.notification_counts || {}) })
        .catch(() => { if (active) setSettlementNotificationCounts({}) })
        .finally(() => { fetching = false })
    }
    refresh()
    const interval = window.setInterval(refresh, 60000)
    window.addEventListener('focus', refresh)
    window.addEventListener('settlement-email-updated', refresh)
    return () => { active = false; window.clearInterval(interval); window.removeEventListener('focus', refresh); window.removeEventListener('settlement-email-updated', refresh) }
  }, [location.pathname, currentRole, currentEmail])

  useEffect(() => {
    let active = true
    const identity = `${user?.id}:${user?.email}:${user?.role}`
    setPendingTaskCounts({})
    const refresh = () => {
      if (!currentEmail) return
      fetchPendingTasks(identity).then((data) => { if (active) setPendingTaskCounts(data.counts || {}) }).catch(() => { if (active) setPendingTaskCounts({}) })
    }
    refresh()
    const interval = window.setInterval(refresh, 60000)
    window.addEventListener('focus', refresh)
    return () => { active = false; window.clearInterval(interval); window.removeEventListener('focus', refresh) }
  }, [location.pathname, currentRole, currentEmail, user?.id])

  const internalRequestNotificationCount = (route) => {
    if (!route?.path) return 0
    const path = route.path.split('?')[0].replace(/\/$/, '')
    const taskByPath = {
      '/sales/deals': 'sales_payment',
      '/buying/po-sourcing': 'buying_sources',
      '/inbound-journey/studio': 'studio_photo',
      '/inbound-journey/appraisal-needed': 'appraisal_needed',
      '/inbound-journey/confirm-buying-price': 'confirm_buying_price',
      '/inbound-journey/appraisal-approval': 'appraisal_approval',
      '/seller-outbound-returns': 'accepted_return'
    }
    if (route.path === '/outbound/deals/list?scope=ready_to_ship') return Number(pendingTaskCounts.outbound_shipment || 0)
    if (taskByPath[path] && !route.path.includes('?')) return Number(pendingTaskCounts[taskByPath[path]] || 0)
    if (route.path === '/product-curated-team/consignor-settlement') return Number(settlementNotificationCounts.consignor_email_pending || 0)

    if (route.path === '/internal-requests/requestor/new') {
      return Number(internalRequestNotificationCounts.requestor_accept || 0)
    }

    if (route.path === '/outbounds/internal-requests/list-page') {
      return Number(internalRequestNotificationCounts.outbound_dispatch || 0)
    }

    if (route.path === '/inbounds/internal-requests/list-page') {
      return Number(internalRequestNotificationCounts.inbound_put_away || 0)
    }

    return 0
  }

  const internalRequestNotificationCountForRoutes = (routesArr) => (
    (routesArr || []).reduce((total, route) => {
      if (!route || route.redirect || route.invisible || !isRouteAllowed(route)) return total
      if (route.collapse) return total + internalRequestNotificationCountForRoutes(route.views)

      return total + internalRequestNotificationCount(route)
    }, 0)
  )

  const renderInternalRequestNotificationBadge = (count) => (
    count > 0
      ? <span className='badge badge-danger badge-pill ml-auto' title={`${count} perlu ditindaklanjuti. Internal Request: receive/put away/dispatch. Settlement: transfer selesai, email belum dikirim atau perlu dikirim ulang. Submenu lainnya: tugas pending sesuai akses tim.`} aria-label={`${count} perlu ditindaklanjuti`}>{ count > 99 ? '99+' : count }</span>
      : null
  )

  const activeRoute = (route) => (isRouteActive(route) ? 'active' : '')

  const closeSidenav = () => {
    if (window.innerWidth < 1080) {
      toggleSidebar()
    }
  }

  // toggle a single collapse group (no global close-all → nested works)
  const toggleCollapse = (stateKey) => {
    if (!stateKey) return
    setCollapseStates((prev) => ({ ...prev, [stateKey]: !prev[stateKey] }))
  }

  // === Generate menu items ===
  const createLinks = (routesArr) =>
    (routesArr || []).map((route, key) => {
      if (route.redirect || route.invisible || !isRouteAllowed(route)) return null

      // ===== GROUP / PARENT (collapse) =====
      if (route.collapse) {
        if (!hasVisibleRoute(route.views)) return null
        const stateKey = route.state_key
        const isOpen = !!collapseStates[stateKey]
        const isParentActive = getCollapseInitialState(route.views)
        const notificationCount = route.state_key === 'product_curated_team_collapse'
          ? Number(settlementNotificationCounts.consignor_email_pending || 0)
          : route.state_key === 'outbound_team_collapse'
            ? Number(internalRequestNotificationCounts.outbound_dispatch || 0)
            : internalRequestNotificationCountForRoutes(route.views)
        const notificationBadge = renderInternalRequestNotificationBadge(notificationCount)

        return (
          <NavItem key={stateKey || route.name || key}>
            <NavLink
              data-toggle='collapse'
              aria-expanded={isOpen}
              className={classnames({ active: isParentActive })}
              onClick={(e) => {
                e.preventDefault()
                toggleCollapse(stateKey) // 🔧 FIX: only toggle this group
              }}
            >
              {route.icon ? (
                <>
                  <i className={route.icon} />
                  <span className='nav-link-text'>{route.name}</span>
                  { notificationBadge }
                </>
              ) : route.miniName ? (
                <>
                  <span className='sidenav-mini-icon'> {route.miniName} </span>
                  <span className='sidenav-normal'> {route.name} </span>
                  { notificationBadge }
                </>
              ) : (
                <>
                  <span>{route.name}</span>
                  { notificationBadge }
                </>
              )}
            </NavLink>

            <Collapse isOpen={isOpen}>
              <Nav className='nav-sm flex-column'>
                {createLinks(route.views)}
              </Nav>
            </Collapse>
          </NavItem>
        )
      }

      // ===== LEAF / SUB-MENU =====
      const fullPath = `${route.layout}${route.path}`
      const isActive = isRouteActive(route)
      const isHovered = hoverItem === fullPath
      const notificationCount = internalRequestNotificationCount(route)
      const notificationBadge = renderInternalRequestNotificationBadge(notificationCount)

      return (
        <NavItem
          className={activeRoute(route)}
          key={route.key || fullPath || key}
        >
          <NavLink
            to={fullPath}
            activeClassName=''
            onClick={closeSidenav}
            tag={NavLinkRRD}
            onMouseEnter={() => setHoverItem(fullPath)}
            onMouseLeave={() => setHoverItem(null)}
            style={{
              backgroundColor: isActive || isHovered ? '#8d9e74' : 'transparent',
              color: isActive || isHovered ? '#fff' : '#000',
              borderRadius: '8px',
              padding: '8px 14px',
              transition: 'all 0.2s ease'
            }}
          >
            {route.icon !== undefined ? (
              <>
                <i className={route.icon} />
                <span className='nav-link-text'>{route.name}</span>
                { notificationBadge }
              </>
            ) : route.miniName !== undefined ? (
              <>
                <span className='sidenav-mini-icon'> {route.miniName} </span>
                <span className='sidenav-normal'> {route.name } </span>
                { notificationBadge }
              </>
            ) : (
              <>
                <span>{ route.name }</span>
                { notificationBadge }
              </>
            )}
          </NavLink>
        </NavItem>
      )
    })

  const handleLogout = async (event) => {
    event && event.preventDefault && event.preventDefault()

    try {
      await AdminsApiV2.logout()
      authContext.onLogout()
      history.replace('/')
    } catch (error) {
      authContext.onLogout()
      history.replace('/')
    }
  }

  const renderScrollBarInner = () => (
    <div className='scrollbar-inner'>
      {/* Sidebar Header */}
      <div className='sidenav-header d-flex align-items-center'>
        <NavbarBrand tag={Link} name={logo.name} sup={logo.sup} to={'/'}>
          <img height={300} width={100} src={logo.imgSrc} alt={logo.imgAlt} />
          <span className='ml-3'>{logo.name}</span>
          {logo.sup && <sup className='font-weight-light'>{logo.sup}</sup>}
        </NavbarBrand>

        <div className='ml-auto'>
          <div
            className={classnames('sidenav-toggler d-none d-xl-block', {
              active: isSidebarOpen
            })}
            onClick={toggleSidebar}
          >
            <div className='sidenav-toggler-inner'>
              <i className='sidenav-toggler-line' />
              <i className='sidenav-toggler-line' />
              <i className='sidenav-toggler-line' />
            </div>
          </div>
        </div>
      </div>

      <div className='navbar-inner'>
        <Collapse navbar isOpen={true}>
          <Nav navbar>
            {createLinks(routes)}
          </Nav>
          <hr className='my-3' />
          <Nav className='mb-md-3' navbar>
            <NavItem>
              <NavLink href="#logout" onClick={handleLogout}>
                <Button outline block color='primary' size='sm'>
                  <i className='fas fa-sign-out-alt' />
                  <span className='nav-link-text'>Logout</span>
                </Button>
              </NavLink>
            </NavItem>
          </Nav>
        </Collapse>
      </div>
    </div>
  )

  const renderScrollBarBody = () => (
    <div
      style={{
        height                   : '100vh',
        maxHeight                : '100vh',
        overflowY                : 'auto',
        overflowX                : 'hidden',
        WebkitOverflowScrolling  : 'touch',
        overscrollBehavior       : 'contain'
      }}
    >
      {renderScrollBarInner()}
    </div>
  )

  return (
    <Navbar
      className='sidenav navbar-vertical navbar-expand-xs navbar-light bg-white fixed-left'
    >
      {renderScrollBarBody()}
    </Navbar>
  )
}

Sidebar.defaultProps = {
  routes        : [ {} ],
  toggleSidebar : () => {},
  isSidebarOpen : false
}

Sidebar.propTypes = propTypes

export default Sidebar
